import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Download, Copy, Plus, Settings2, UserRoundCheck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatDate } from '@/lib/utils';
import { useHasPermission, useSession } from '@/hooks/useSession';
import { useAutoLocateFilteredPage } from '@/shared/hooks/useAutoLocateFilteredPage';
import { CreateUserModal, UserFormModal } from '@/features/users/components/modals';
import { useUpdateUser, useUser } from '@/features/users/hooks';
import { USER_PERMISSIONS } from '@/features/users/permissions';
import { getAccount, getAccountUsers, syncAccountUsersFromMongo } from '../api';
import { AssignTechnicianDialog } from './AssignTechnicianDialog';
import type { UserDetail } from '../types';
import type { UpdateUserInput } from '@/features/users/types';

interface UsersTabProps {
  accountId: string;
  organisationId?: string | null;
}

const PAGE_SIZE = 10;

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

interface ColumnDef {
  key: string;
  label: string;
  defaultVisible?: boolean;
  filterType?: 'text' | 'select';
  filterOptions?: { label: string; value: string }[];
  getValue: (user: UserDetail) => string | number | null;
  render?: (user: UserDetail) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

function buildColumns(
  distinctRoles: string[],
  distinctTags: string[],
  onEditUser: (userId: string) => void,
): ColumnDef[] {
  return [
    {
      key: 'name',
      label: 'Name',
      filterType: 'text',
      getValue: (u) =>
        [u.firstname, u.lastname].filter(Boolean).join(' ') || '(No name)',
      render: (u) => (
        <span className="font-medium">
          {[u.firstname, u.lastname].filter(Boolean).join(' ') || '(No name)'}
        </span>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      filterType: 'text',
      getValue: (u) => u.email || '',
      render: (u) =>
        u.email ? (
          <a
            href={`mailto:${u.email}`}
            className="text-atek-blue hover:underline text-sm"
          >
            {u.email}
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'phone',
      label: 'Phone',
      filterType: 'text',
      defaultVisible: false,
      getValue: (u) => u.phone || u.mobile || '',
      render: (u) => (
        <span className="text-sm">{u.phone || u.mobile || '—'}</span>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      filterType: 'select',
      filterOptions: distinctRoles.map((r) => ({
        label: formatRole(r),
        value: r,
      })),
      getValue: (u) => u.role || '',
      render: (u) => <span className="text-sm">{formatRole(u.role)}</span>,
    },
    {
      key: 'tags',
      label: 'Tags',
      filterType: 'select',
      filterOptions: distinctTags.map((t) => ({
        label: t,
        value: t,
      })),
      getValue: (u) => (u.tags || []).join(', '),
      render: (u) =>
        u.tags && u.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {u.tags.map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs font-normal">
                {tag}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        ),
    },
    {
      key: 'usergroups',
      label: 'User Groups',
      filterType: 'text',
      defaultVisible: false,
      getValue: (u) => u.usergroups?.map((ug) => ug.name).join(', ') || '',
      render: (u) => {
        const names =
          u.usergroups?.map((ug) => ug.name).join(', ') || '—';
        return (
          <span
            className="text-sm max-w-[200px] truncate block"
            title={names}
          >
            {names}
          </span>
        );
      },
    },
    {
      key: 'lastLogin',
      label: 'Last Login',
      filterType: 'text',
      getValue: (u) => u.login_at || '',
      render: (u) => (
        <span className="text-sm">
          {u.login_at ? formatDate(u.login_at) : 'Never'}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      filterType: 'select',
      filterOptions: [
        { label: 'Enabled', value: 'Enabled' },
        { label: 'Disabled', value: 'Disabled' },
      ],
      getValue: (u) => (u.enabled ? 'Enabled' : 'Disabled'),
      render: (u) => (
        <Badge variant={u.enabled ? 'success' : 'secondary'}>
          {u.enabled ? 'Enabled' : 'Disabled'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: '',
      noFilter: true,
      getValue: () => '',
      render: (u) => (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-atek-blue hover:text-atek-blue"
          onClick={() => onEditUser((u.id ?? u.pg_id ?? '') as string)}
        >
          Edit
        </Button>
      ),
      align: 'right',
    },
  ];
}

// Static columns reference (for visibility defaults — no dynamic options needed)
const STATIC_COLUMNS = buildColumns([], [], () => undefined);

// ---------------------------------------------------------------------------
// Column visibility persistence
// ---------------------------------------------------------------------------

const VISIBILITY_STORAGE_KEY = 'atek-account-users-column-visibility';
const FILTER_STORAGE_KEY = 'atek-account-users-column-filters';

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of STATIC_COLUMNS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored)
      return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch {
    /* ignore */
  }
  return getDefaultVisibility();
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(
    VISIBILITY_STORAGE_KEY,
    JSON.stringify(visibility),
  );
}

// ---------------------------------------------------------------------------
// CSV / clipboard export
// ---------------------------------------------------------------------------

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function exportToCSV(
  items: UserDetail[],
  columns: ColumnDef[],
  filename: string,
): void {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols
    .map((c) => escapeCSVValue(c.label))
    .join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSVValue(c.getValue(item))).join(','),
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function copyToClipboard(
  items: UserDetail[],
  columns: ColumnDef[],
): Promise<void> {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols
      .map((c) => String(c.getValue(item) ?? ''))
      .join('\t'),
  );
  const text = [header, ...rows].join('\n');
  return navigator.clipboard.writeText(text);
}

// ---------------------------------------------------------------------------
// Filter row renderer
// ---------------------------------------------------------------------------

function renderColumnFilter(
  col: ColumnDef,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try {
        return columnFilters[col.key]
          ? JSON.parse(columnFilters[col.key])
          : [];
      } catch {
        return [];
      }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(
        col.key,
        next.length ? JSON.stringify(next) : '',
      );
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-full text-xs justify-start font-normal"
            >
              {selected.length
                ? `${selected.length} selected`
                : '—'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function UsersTab({
  accountId,
  organisationId,
}: UsersTabProps) {
  const [page, setPage] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignTechnician, setShowAssignTechnician] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const { user: sessionUser } = useSession();
  const isFullAdmin = !!(sessionUser?.postgres_roles?.includes('full_admin') || sessionUser?.postgres_roles?.includes('director'));
  // Mirrors the web_api_server contact-info tier: sales_manager may edit
  // contact fields (incl. email) on the users this tab lists.
  const isContactManager = !!sessionUser?.postgres_roles?.includes('sales_manager');
  const isSuperAdmin = isFullAdmin;
  const hasCreateUserPermission = useHasPermission(USER_PERMISSIONS.create);
  const canCreateUser = isFullAdmin || hasCreateUserPermission;
  const { data: accountRecord } = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => getAccount(accountId),
    enabled: !!accountId,
  });
  const editingUserQuery = useUser(editingUserId || undefined);
  const updateUserMutation = useUpdateUser();
  const resolvedOrganisationId =
    accountRecord?.mongo_organisation_id || organisationId || undefined;

  // Column visibility
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(loadColumnVisibility);

  const toggleColumn = (key: string) => {
    const updated = {
      ...columnVisibility,
      [key]: !columnVisibility[key],
    };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };

  const isVisible = (key: string) => columnVisibility[key] !== false;

  // Client-side column filters (refinement within current page)
  const [columnFilters, setColumnFilters] = useState<
    Record<string, string>
  >(() => {
    try {
      return JSON.parse(
        localStorage.getItem(FILTER_STORAGE_KEY) || '{}',
      );
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify(columnFilters),
    );
  }, [columnFilters]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  const queryClient = useQueryClient();
  // NOTE: the rest of this component still hardcodes English. Only the strings
  // touched here go through t(); converting the whole tab is its own change.
  const { t } = useTranslation('users');
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['account-users', accountId, page],
    queryFn: () =>
      getAccountUsers(accountId, {
        pageNumber: page,
        pageLength: PAGE_SIZE,
      }),
    enabled: !!accountId,
  });

  // Mongo→PG sync, triggered EXPLICITLY by the button in the card header.
  //
  // This used to run from a bare mount effect, which made merely opening the tab
  // perform a cross-account WRITE (the route updates usergroups, upserts rows in
  // the global users table and deletes stale usergroup_members). That was masked
  // while the route 403'd for most staff; now that it does not, a navigation must
  // not move data. Same shape as SensorsTab's "Refresh from MongoDB".
  const syncMutation = useMutation({
    mutationFn: (id: string) => syncAccountUsersFromMongo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-users', accountId] });
      toast.success(t('sync.success', 'Users refreshed from MongoDB'));
    },
    onError: (e) => {
      toast.error(t('sync.error', 'Failed to refresh users from MongoDB'), {
        description: e instanceof Error ? e.message : 'Unexpected error',
      });
    },
  });

  const users = data?.data || [];

  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;
  const distinctTags = data?.distinctTags || [];
  const distinctRoles = data?.distinctRoles || [];

  const handleUpdateUser = async (userData: UpdateUserInput) => {
    if (!editingUserId) return;

    await updateUserMutation.mutateAsync({
      userId: editingUserId,
      data: userData,
    });
  };

  // Build columns with dynamic filter options from server response
  const columns = useMemo(
    () => buildColumns(distinctRoles, distinctTags, setEditingUserId),
    [distinctRoles, distinctTags],
  );

  const visibleColumns = columns.filter((col) => isVisible(col.key));

  // Client-side column filter refinement within page
  const filteredItems = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(
      ([, v]) => v,
    );
    if (activeFilters.length === 0) return users;

    return users.filter((item) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = columns.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(item);

        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(
              filterValue,
            ) as string[];
            if (selected.length === 0) return true;
            // Tags column: a user can have multiple tags, check if any match
            if (col.key === 'tags') {
              const userTags = String(rawValue || '').split(', ').filter(Boolean);
              return userTags.some((t) => selected.includes(t));
            }
            return selected.includes(String(rawValue || ''));
          } catch {
            return true;
          }
        }

        return String(rawValue ?? '')
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      }),
    );
  }, [users, columnFilters, columns]);

  const hasActiveColumnFilters = useMemo(
    () => Object.values(columnFilters).some(Boolean),
    [columnFilters],
  );

  useAutoLocateFilteredPage({
    hasActiveFilters: hasActiveColumnFilters,
    filterSignature: JSON.stringify(columnFilters),
    filteredCount: filteredItems.length,
    pageItemCount: users.length,
    page,
    totalPages,
    isLoading,
    onPageChange: setPage,
  });

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center space-y-4">
          <p className="text-destructive">Error loading users</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error
              ? error.message
              : 'An unexpected error occurred'}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Users</CardTitle>
              <CardDescription>
                {filteredItems.length < users.length
                  ? `Showing ${filteredItems.length} of ${users.length} on this page`
                  : `Showing ${users.length} of ${total}`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => accountId && syncMutation.mutate(accountId)}
                disabled={!resolvedOrganisationId || syncMutation.isPending}
                title={
                  resolvedOrganisationId
                    ? undefined
                    : 'This account is not linked to a Mongo organisation'
                }
              >
                <RefreshCw
                  className={`h-4 w-4 mr-1${syncMutation.isPending ? ' animate-spin' : ''}`}
                />
                {syncMutation.isPending
                  ? t('sync.running', 'Refreshing...')
                  : t('sync.action', 'Refresh from MongoDB')}
              </Button>
              {canCreateUser && (
                <Button
                  size="sm"
                  onClick={() => setShowAssignTechnician(true)}
                  disabled={!resolvedOrganisationId}
                  title={
                    resolvedOrganisationId
                      ? undefined
                      : 'This account is not linked to a Mongo organisation'
                  }
                >
                  <UserRoundCheck className="h-4 w-4 mr-1" /> Assign Technician
                </Button>
              )}
              {canCreateUser && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCreateModal(true)}
                  disabled={!resolvedOrganisationId}
                  title={
                    resolvedOrganisationId
                      ? undefined
                      : 'This account is not linked to a Mongo organisation'
                  }
                >
                  <Plus className="h-4 w-4 mr-1" /> New User
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  copyToClipboard(filteredItems, columns);
                  toast.success('Copied to clipboard');
                }}
              >
                <Copy className="h-4 w-4 mr-1" /> Copy
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  exportToCSV(
                    filteredItems,
                    columns,
                    'account-users',
                  );
                  toast.success(
                    `Exported ${filteredItems.length} rows`,
                  );
                }}
              >
                <Download className="h-4 w-4 mr-1" /> CSV
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Settings2 className="h-4 w-4 mr-1" />{' '}
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>
                    Toggle columns
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {columns.filter((c) => !c.noFilter).map(
                    (col) => (
                      <DropdownMenuCheckboxItem
                        key={col.key}
                        checked={isVisible(col.key)}
                        onCheckedChange={() =>
                          toggleColumn(col.key)
                        }
                        onSelect={(e) => e.preventDefault()}
                      >
                        {col.label}
                      </DropdownMenuCheckboxItem>
                    ),
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                {/* Row 1: Column filters */}
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  {visibleColumns.map((col) =>
                    renderColumnFilter(
                      col,
                      columnFilters,
                      setColumnFilter,
                    ),
                  )}
                </TableRow>

                {/* Row 2: Column labels */}
                <TableRow>
                  {visibleColumns.map((col) => (
                    <TableHead
                      key={col.key}
                      className={cn(
                        col.align === 'right' &&
                          'text-right',
                      )}
                    >
                      {col.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>

              <TableBody>
                {/* Loading state */}
                {isLoading &&
                  Array.from({ length: 10 }).map((_, i) => (
                    <TableRow key={i}>
                      {visibleColumns.map((col) => (
                        <TableCell key={col.key}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}

                {/* Empty state */}
                {!isLoading && filteredItems.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={visibleColumns.length}
                      className="py-12 text-center text-muted-foreground"
                    >
                      No users found
                    </TableCell>
                  </TableRow>
                )}

                {/* Data rows */}
                {!isLoading &&
                  filteredItems.map((user) => (
                    <TableRow
                      key={user._id}
                      className="hover:bg-muted/50"
                    >
                      {visibleColumns.map((col) => (
                        <TableCell
                          key={col.key}
                          className={cn(
                            'text-sm',
                            col.align === 'right' &&
                              'text-right',
                            col.className,
                          )}
                        >
                          {col.render
                            ? col.render(user)
                            : String(
                                col.getValue(user) ?? '',
                              ) || '—'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination (server-side) */}
          {totalPages > 1 && (
            <div className="border-t px-4 py-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateUserModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        accountId={accountId}
        defaultOrganisationId={resolvedOrganisationId}
        lockOrganisation
      />

      {resolvedOrganisationId && (
        <AssignTechnicianDialog
          open={showAssignTechnician}
          onOpenChange={setShowAssignTechnician}
          accountId={accountId}
          accountName={accountRecord?.name || 'this account'}
          organisationId={resolvedOrganisationId}
        />
      )}

      <UserFormModal
        open={!!editingUserId}
        onOpenChange={(open) => {
          if (!open) setEditingUserId(null);
        }}
        user={editingUserQuery.data ?? null}
        onSubmit={handleUpdateUser}
        isSubmitting={updateUserMutation.isPending}
        canDeleteEntirely={isSuperAdmin}
        isFullAdmin={isFullAdmin}
        canEditEmail={isFullAdmin || isContactManager}
        accountId={accountId}
        initialOrganisationId={resolvedOrganisationId}
        lockOrganisation
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRole(role?: string): string {
  if (!role) return '—';
  return role
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
